<template>
    <b-modal
        size="md"
        v-model="modalShow"
        :title="`所属${$locale.patient}の追加`"
        @ok="handleOk"
        @cancel="close"
        @hidden="close">
        <form
            class="adminGroupUsersStoreModalWrapper"
            @submit.stop.prevent="store">
            <div class="row">
                <div class="col-12">
                    <div class="form-group m-form__group">
                        <div class="text-center">「{{ group.name }}」に所属させる{{ $locale.patient }}を選択してください</div>
                        <div class="d-flex justify-content-center mt-3">
                            <div>
                                <div
                                    v-for="(id, idx) in selectedUsers"
                                    class="mb-2">
                                    <multiselect
                                        v-model="selectedUsers[idx]"
                                        :options="filteredUsers"
                                        :placeholder="`${$locale.patient}を選択してください`"
                                        :multiple="false"
                                        :close-on-select="true"
                                        :clear-on-select="true"
                                        :hide-selected="true"
                                        :preserve-search="true"
                                        :disabled="inProgress"
                                        selectLabel="選択　"
                                        label="label"
                                        track-by="id"
                                        @input="onInputMultiSelect">
                                        <template v-slot:noOptions>{{ `選択できる${$locale.patient}がいません` }}</template>
                                        <template v-slot:noResult>{{ `${$locale.patient}が見つかりません` }}</template>
                                    </multiselect>
                                    <div
                                        v-if="errors[`user_ids.${idx}`]"
                                        class="invalid-feedback d-block mt-1">
                                        {{ errors[`user_ids.${idx}`][0] }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            v-if="errors.message"
                            class="invalid-feedback d-block mt-1 text-center">
                            {{ errors.message[0] }}
                        </div>
                    </div>
                </div>
            </div>
        </form>

        <div
            slot="modal-footer"
            class="w-100" :class="{ 'u-loader u-loader--sm' : inProgress }">
            <button
                class="btn btn-yokumiru-orange float-right"
                :disabled="inProgress || isEmptySelectedUsers"
                @click="handleOk">
                追加
            </button>
            <button
                class="btn btn-yokumiru-gray float-right mr-2"
                :disabled="inProgress"
                @click="close">
                キャンセル
            </button>
        </div>
    </b-modal>
</template>

<script>
    import constants from '../../../../constants'
    import YokumiruHelper from '../../../../YokumiruHelper'
    import Multiselect from 'vue-multiselect'

    export default {
        components: { Multiselect },

        props: {
            parentGroup: {
                type: Object,
                required: true,
            },
            group: {
                type: Object,
                required: true,
            },
            joinedUsers: {
                type: Array,
                default: [],
            },
        },

        data: function () {
            return {
                helper: YokumiruHelper,
                inProgress: false,
                modalShow: false,
                users: [],
                selectedUsers: [null],
                errors: {},
            }
        },

        computed: {
            /**
             * 会員が誰も選択されていない
             *
             * @return {boolean}
             */
            isEmptySelectedUsers: function () {
                const selected = this.selectedUsers.filter((user) => user && user.id !== null)
                return selected.length < 1
            },

            /**
             * 選択された会員IDリスト
             *
             * @return {array<number>}
             */
            selectedUserIds: function () {
                return this.selectedUsers.map((user) => user ? user.id : null)
            },

            /**
             * グループに所属中の会員IDのリスト
             */
            joinedUserIds: function () {
                return this.joinedUsers.map((user) => user.id)
            },

            /**
             * 選択可能な会員のリスト
             */
            filteredUsers: function () {
                return this.users.filter((user) => {
                    if (user.id === this.parentGroup.authorizer_id) {
                        // 親グループの担当者は除外
                        return false
                    }
                    else if ( this.helper.isCompany(this.group) && user.is_company ) {
                        // 子グループに所属済みの会員は除外
                        return false
                    }
                    else {
                        // グループ所属していない個人会員は選択可能
                        return !this.joinedUserIds.includes(user.id) && !this.selectedUserIds.includes(user.id)
                    }
                })
            },
        },

        created: function () {
            this.fetchUsersMultiselect()
            bus.$on('open-modal-authorizer-child-group-users-store', this.open)
        },

        beforeDestroy: function () {
            bus.$off('open-modal-authorizer-child-group-users-store')
        },

        methods: {
            /**
             * 会員選択リストを取得
             */
            fetchUsersMultiselect: function () {
                const endpoint = constants.user.groups.endpoint.base

                axios.get(`${endpoint}/${this.parentGroup.id}/users/multiselect`)
                    .then(({data}) => {
                        this.users = data.data
                        this.users.unshift({
                            id: null,
                            full_name: '',
                            full_name_kana: '',
                            label: `${this.$locale.patient}を選択してください`,
                        })
                    })
            },

            /**
             * 会員選択時に末尾にmultiselect要素を追加
             *
             * @param {object} user
             */
            onInputMultiSelect: function (user) {
                if (!user) return

                if (this.selectedUsers[this.selectedUsers.length - 1] !== null) {
                    this.selectedUsers.push(null)
                }
            },

            /**
             * モーダル表示
             */
            open: function () {
                this.modalShow = true
            },

            /**
             * モーダルを閉じる
             */
            close: function () {
                this.modalShow = false
                this.errors = {}
                this.selectedUsers = [null]
            },

            /**
             * ボタン押下時
             *
             * @param {object} e
             */
            handleOk: function (e) {
                e.preventDefault()
                this.store()
            },

            /**
             * 所属会員追加
             */
            store: async function () {
                if (this.inProgress) return
                this.inProgress = true

                const endpoint = constants.user.groups.endpoint.base
                const userIds = this.selectedUsers.map(user => user ? user.id : null).filter(v => v)

                await axios.post(`${endpoint}/${this.parentGroup.id}/groups/${this.group.id}/users`, { user_ids: userIds })
                    .then(() => {
                        bus.$emit('alert-success', `所属${ this.$locale.patient }を追加しました。`)
                        bus.$emit('group-users-store')
                        this.errors = {}
                        this.selectedUsers = [null]
                        this.fetchUsersMultiselect()
                        this.close()
                    })
                    .catch(({response}) => {
                        if (response.hasOwnProperty('data') && response.data.hasOwnProperty('errors')) {
                            bus.$emit('alert-danger')
                            this.errors = response.data.errors
                        }
                        else {
                            bus.$emit('alert-danger', 'システムエラーが発生しました。')
                        }
                    })

                this.inProgress = false
            },
        },
    }
</script>

<style lang="scss">
.adminGroupUsersStoreModalWrapper {
    .multiselect {
        width: 350px;
    }
}
</style>

<style>
@import '~vue-multiselect/dist/vue-multiselect.min.css';
</style>
